'use client'

import React from 'react'
import { cn } from '@/lib/utils'

interface LogomarkProps {
  className?: string
  /**
   * 'dark' uses a navy-dominant palette (for light backgrounds)
   * 'light' uses a cornflower-dominant palette (for dark backgrounds)
   */
  variant?: 'dark' | 'light'
  size?: number
}

/**
 * Custom Swaastha AI Geometric Health Blossom Logo.
 * Designed to scale cleanly and inherit colors dynamically based on dark/light themes.
 */
export function Logomark({
  className,
  variant = 'dark',
  size = 40,
}: LogomarkProps) {
  const primaryColor = variant === 'light' ? '#8AA2DF' : '#141A42'
  const strokeColor =
    variant === 'light' ? 'rgba(138, 162, 223, 0.7)' : 'rgba(20, 26, 66, 0.6)'
  const leafFill =
    variant === 'light' ? 'rgba(20, 26, 66, 0.75)' : 'rgba(138, 162, 223, 0.25)'
  const centerFill = variant === 'light' ? '#535EA4' : '#8AA2DF'

  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 200 200'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={cn('shrink-0 select-none', className)}
      aria-hidden='true'
    >
      <circle cx='100' cy='100' r='85' stroke={strokeColor} strokeWidth='2' />

      <line x1='100' y1='15' x2='100' y2='185' stroke={strokeColor} strokeWidth='2' />
      <line x1='15' y1='100' x2='185' y2='100' stroke={strokeColor} strokeWidth='2' />
      <line
        x1='40'
        y1='40'
        x2='160'
        y2='160'
        stroke={strokeColor}
        strokeWidth='1.5'
        strokeDasharray='3 3'
      />
      <line
        x1='160'
        y1='40'
        x2='40'
        y2='160'
        stroke={strokeColor}
        strokeWidth='1.5'
        strokeDasharray='3 3'
      />

      <g>
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          transform='rotate(60 100 100)'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          transform='rotate(120 100 100)'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          transform='rotate(180 100 100)'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          transform='rotate(240 100 100)'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
        <path
          d='M 100 100 C 120 75, 155 75, 175 100 C 155 125, 120 125, 100 100 Z'
          transform='rotate(300 100 100)'
          fill={leafFill}
          stroke={primaryColor}
          strokeWidth='3.5'
          strokeLinejoin='round'
        />
      </g>

      <circle
        cx='100'
        cy='100'
        r='24'
        fill={centerFill}
        stroke={variant === 'light' ? '#141A42' : '#FFFFFF'}
        strokeWidth='2'
      />

      <circle cx='100' cy='100' r='8' fill='#FFFFFF' />
    </svg>
  )
}
